<template>
  <div class="filter-panel-root">
  <transition name="panel-slide">
    <div class="filter-panel" v-show="!collapsed" :class="{ 'panel-mobile': isMobile }">
      <!-- Header -->
      <div class="panel-header">
        <div class="panel-header-left">
          <v-icon size="16" color="#374151">mdi-filter-variant</v-icon>
          <span class="panel-title">Map Filters</span>
        </div>
        <button class="panel-collapse-btn" @click="collapsed = true" title="Close">
          <v-icon size="16" color="#6b7280">mdi-close</v-icon>
        </button>
      </div>
      <!-- Tabs -->
      <div class="panel-tabs">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          class="tab-btn"
          :class="{ active: activeTab === tab.id }"
          @click="setTab(tab.id)"
        >{{ tab.label }}</button>
      </div>
      <!-- Custom Filters (Injected from Page) -->
      <div v-if="$slots.default" class="panel-body custom-filters-container" style="padding-bottom: 0;">
        <div class="section-label">SEARCH FILTERS</div>
        <slot></slot>
        <div class="section-divider mt-3 mb-1" style="border-bottom: 1px solid #f3f4f6;"></div>
      </div>
      <!-- Markers tab -->
      <div v-if="activeTab === 'markers'" class="panel-body">
        <div class="section-label">LAYERS</div>
        <div
          v-for="layer in layers"
          :key="layer.id"
          class="layer-row"
        >
          <span class="layer-dot" :style="{ background: layer.color }"></span>
          <span class="layer-name">{{ layer.title }}</span>
          <label class="custom-toggle">
            <input 
              type="checkbox" 
              v-model="localVisibility[layer.id]"
              @change="onToggle(layer.id, localVisibility[layer.id])"
            />
            <span class="toggle-track"></span>
          </label>
        </div>

      </div>
      <!-- Clusters tab -->
      <div v-if="activeTab === 'clusters'" class="panel-body">
        <div class="section-label">CLUSTERING</div>
        <p class="panel-hint">Markers are automatically grouped into clusters when zoomed out. Zoom in to see individual markers.</p>
      </div>
      <!-- Heatmap tab -->
      <div v-if="activeTab === 'heatmap'" class="panel-body">
        <div class="section-label">HEATMAP</div>
        <p class="panel-hint">Heatmap visualisation coming soon.</p>
      </div>
    </div>
  </transition>
  <!-- Collapsed toggle button -->
  <transition name="btn-fade">
    <button
      v-show="collapsed"
      class="filter-toggle-btn"
      @click="collapsed = false"
      title="Show filters"
    >
      <v-icon size="20" color="#374151">mdi-filter-variant</v-icon>
    </button>
  </transition>
  </div>
</template>
<script>
export default {
  name: 'MapFilterPanel',
  props: {
    layers: {
      type:    Array,
      default: () => [],
    },
  },
  data() {
    return {
      collapsed:       false,
      activeTab:       'markers',
      localVisibility: {},
      tabs: [
        { id: 'markers',  label: 'Markers' },
        { id: 'clusters', label: 'Clusters' },
        { id: 'heatmap',  label: 'Heatmap' },
      ],
    };
  },
  computed: {
    isMobile() {
      return this.$vuetify && this.$vuetify.breakpoint.smAndDown;
    },

  },
  watch: {
    layers: {
      immediate: true,
      handler(layers) {
        layers.forEach(l => {
          if (!(l.id in this.localVisibility)) {
            this.$set(this.localVisibility, l.id, l.visible !== false);
          }
        });
      },
    },
  },
  mounted() {
    this.$emit('display-mode-change', this.activeTab);
  },
  methods: {
    onToggle(layerId, visible) {
      this.$emit('layer-toggle', { layerId, visible });
    },
    setTab(tabId) {
      this.activeTab = tabId;
      this.$emit('display-mode-change', tabId);
    },
  },
};
</script>
<style scoped>
/* ── Panel ─────────────────────────────────────────────────────────────────── */
.filter-panel {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 1200;
  width: 276px;
  background: #FFFFFF;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  border: 1px solid #EEF0F3;
  overflow: hidden;
  max-height: calc(100% - 80px);
  overflow-y: auto;
}
.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px 8px 14px;
  border-bottom: 1px solid #f3f4f6;
}
.panel-header-left {
  display: flex;
  align-items: center;
  gap: 6px;
}
.panel-title {
  font-family: poppinsmedium, 'Poppins', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #1f2937;
}
.panel-collapse-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}
.panel-collapse-btn:hover { background: #F5F6FA; }
/* ── Tabs ───────────────────────────────────────────────────────────────────── */
.panel-tabs {
  display: flex;
  gap: 4px;
  padding: 4px;
  margin: 12px 14px;
  background: #F1F2F6;
  border-radius: 20px;
}
.tab-btn {
  flex: 1;
  padding: 6px 0;
  border: none;
  background: transparent;
  font-family: poppinsmedium, 'Poppins', sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: #6B7280;
  cursor: pointer;
  border-radius: 16px;
  transition: background 0.2s, color 0.2s;
}
.tab-btn.active {
  background: #fb8c00;
  color: #FFFFFF;
}
/* ── Body ───────────────────────────────────────────────────────────────────── */
.panel-body { padding: 12px 14px; }
.section-label {
  font-family: poppinsmedium, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #9CA3AF;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  margin-bottom: 8px;
}
.layer-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px solid #f9fafb;
}
.layer-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
.layer-name {
  flex: 1;
  font-family: poppinsregular, sans-serif;
  font-size: 13px;
  color: #374151;
  line-height: 1;
}

/* ── Custom Toggle Switch ─────────────────────────────────────────────────── */
.custom-toggle {
  position: relative;
  display: inline-block;
  width: 32px;
  height: 18px;
  margin-left: auto;
}
.custom-toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}
.toggle-track {
  position: absolute;
  cursor: pointer;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: #E5E7EB;
  transition: .2s;
  border-radius: 9px;
}
.toggle-track:before {
  position: absolute;
  content: "";
  height: 14px;
  width: 14px;
  left: 2px;
  bottom: 2px;
  background-color: #FFFFFF;
  transition: .2s;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0,0,0,0.15);
}
.custom-toggle input:checked + .toggle-track {
  background-color: #fb8c00;
}
.custom-toggle input:checked + .toggle-track:before {
  transform: translateX(14px);
}
.panel-hint {
  font-family: poppinsregular, sans-serif;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}
/* ── Collapsed toggle btn ──────────────────────────────────────────────────── */
.filter-toggle-btn {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 1200;
  width: 40px;
  height: 40px;
  border: none;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(14px);
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  transition: transform 0.15s;
}
.filter-toggle-btn:hover { transform: scale(1.05); }
/* ── Transitions ───────────────────────────────────────────────────────────── */
.panel-slide-enter-active, .panel-slide-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}
.panel-slide-enter, .panel-slide-leave-to {
  opacity: 0;
  transform: translateX(20px);
}
.btn-fade-enter-active, .btn-fade-leave-active { transition: opacity 0.2s; }
.btn-fade-enter, .btn-fade-leave-to { opacity: 0; }
/* ── Mobile bottom sheet ───────────────────────────────────────────────────── */
@media (max-width: 600px) {
  .filter-panel {
    top: auto;
    bottom: 0;
    right: 0;
    left: 0;
    width: 100%;
    border-radius: 16px 16px 0 0;
    max-height: 60vh;
  }
  .filter-toggle-btn {
    bottom: 16px;
    top: auto;
  }
}
@media (min-width: 601px) and (max-width: 960px) {
  .filter-panel { width: 240px; }
}
</style>
 


